/* eslint-disable */
import {
	useRef, useState, useEffect,
	useCallback
} from 'react';
import { Menu, MenuItem, Select } from '@mui/material';
import { SelectPicker } from 'rsuite';
import { useLocation } from 'react-router-dom';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SaveIcon from '@mui/icons-material/Save';
import './LabelGenerator.scss';
import labelGeneratorServices from '../../Services/labelGeneratorServices';
import { notificationServices } from '../../Services';
import { urls } from '../../Constants';
import labelGeneratorConfig, {
	MM_TO_PX,
	GRID_MAX_ROWS,
	GRID_MAX_COLS,
	DENSITY_OPTIONS,
	LABEL_SIZE_PRESETS,
	fontFamilyOptions,
	lineStyleOptions,
	muiSelectSx,
	ActiveStyle,
	tableCellActiveStyles,
	renderLinePreview,
	migrateLabelComponents,
	DEFAULT_TOOL_STATE,
	withBorderDefaults,
} from '../../Components/LabelGenerator/LabelGenerator.config';
import { Rnd } from 'react-rnd';
import { DeleteOutline, FormatBold, RotateRight, ZoomIn, ZoomOut, Crop, SwapHoriz, Edit, Highlight, FormatAlignLeft, FormatAlignCenter, FormatAlignRight, FormatAlignJustify, Undo, Redo } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { modalActions } from '../../Actions';
import { ImageCropModal } from '../../Components/Modals/ImageCropModal';
import DragDropImage from '../../Components/DragDropImage/dragDropImage';
import { inputHelper } from '../../Helpers/utils';
import qrCodeImg from '../../Images/LabelGenerator/qrCode.jpg';
import barCodeImg from '../../Images/LabelGenerator/barCode.jpg';
import LabelTable, { buildCells } from './labelTable';
import RichTextBox from './RichTextBox';
import { generateZpl } from './labelZpl';
import { parseZplToLabel } from './zplToComponents';
import { SquarePen } from 'lucide-react';
import ConfirmationModal from '../../Components/Modals/MultiPoModal/ConfirmationModal';

const alignOptions = [
	{ value: 'left', label: 'Left', Icon: FormatAlignLeft },
	{ value: 'center', label: 'Center', Icon: FormatAlignCenter },
	{ value: 'right', label: 'Right', Icon: FormatAlignRight },
	{ value: 'justify', label: 'Justify', Icon: FormatAlignJustify },
];

const LabelGenerator = () => {
	const history = useHistory();
	const location = useLocation();
	const dispatch = useDispatch();
	const [labelDetails, setLabelDetails] = useState({
		size: { width: 100, height: 60 },
		name: '',
		printCount: 1,
		dpmm: 8,
		labelType: 'MACHINE',
		position: { x: 413, y: 61 },
		borderColor: '#000000',
		thickness: 1,
		borderStyle: 'none',
		lineStyle: 'none',
	});
	const [anchorEl, setAnchorEl] = useState(null);
	const [selectedComponent, setSelectedComponent] = useState({ name: 'label' });
	const [currentLabelComponents, setCurrentLabelComponents] = useState([]);
	const [editingId, setEditingId] = useState(null);
	const [previewMode, setPreviewMode] = useState(false);
	const [zplPreview, setZplPreview] = useState({ loading: false, url: '', error: '' });
	const [zoom, setZoom] = useState(1);
	const savedSelectionRef = useRef(null);
	const canvasScrollRef = useRef(null);
	const [placeholderAnchor, setPlaceholderAnchor] = useState(null);
	const [tableLayoutAnchor, setTableLayoutAnchor] = useState(null);
	const [hoverGrid, setHoverGrid] = useState({ rows: 0, cols: 0 });
	const [toolState, setToolState] = useState(DEFAULT_TOOL_STATE);
	const [guide, setGuide] = useState({
		visible: false, x: 0, y: 0, dotX: 0, dotY: 0,
	});
	const [snapLines, setSnapLines] = useState({ v: [], h: [] });
	const clickPtRef = useRef({ x: 0, y: 0 });
	const historyRef = useRef({ past: [], future: [] });
	const skipHistoryRef = useRef(false);
	const prevComponentsRef = useRef(currentLabelComponents);
	const [histLen, setHistLen] = useState({ past: 0, future: 0 });
	const HISTORY_LIMIT = 50;
	const [deleteConfirmId, setDeleteConfirmId] = useState(null);

	useEffect(() => {
		if (prevComponentsRef.current === currentLabelComponents) return;
		if (skipHistoryRef.current) {
			skipHistoryRef.current = false;
			prevComponentsRef.current = currentLabelComponents;
			return;
		}
		const h = historyRef.current;
		h.past.push(prevComponentsRef.current);
		if (h.past.length > HISTORY_LIMIT) h.past.shift();
		h.future = [];
		prevComponentsRef.current = currentLabelComponents;
		setHistLen({ past: h.past.length, future: h.future.length });
	}, [currentLabelComponents]);

	const handleUndo = useCallback(() => {
		const h = historyRef.current;
		if (!h.past.length) return;
		const prev = h.past.pop();
		h.future.push(prevComponentsRef.current);
		skipHistoryRef.current = true;
		setCurrentLabelComponents(prev);
		setSelectedComponent({ name: 'label' });
		setEditingId(null);
		setHistLen({ past: h.past.length, future: h.future.length });
	}, []);

	const handleRedo = useCallback(() => {
		const h = historyRef.current;
		if (!h.future.length) return;
		const next = h.future.pop();
		h.past.push(prevComponentsRef.current);
		skipHistoryRef.current = true;
		setCurrentLabelComponents(next);
		setSelectedComponent({ name: 'label' });
		setEditingId(null);
		setHistLen({ past: h.past.length, future: h.future.length });
	}, []);

	const handleLabelMouseMove = (event) => {
		if (previewMode) return;
		const rect = event.currentTarget.getBoundingClientRect();
		const z = zoom || 1;
		const widthPx = rect.width / z || 1;
		const heightPx = rect.height / z || 1;
		const xPx = (event.clientX - rect.left) / z;
		const yPx = (event.clientY - rect.top) / z;
		const dpmm = Number(labelDetails?.dpmm) || 8;
		const widthMm = Number(labelDetails?.size?.width) || 0;
		const heightMm = Number(labelDetails?.size?.height) || 0;
		setGuide({
			visible: true,
			x: xPx,
			y: yPx,
			dotX: Math.round((xPx / widthPx) * widthMm * dpmm),
			dotY: Math.round((yPx / heightPx) * heightMm * dpmm),
		});
	};

	const handleLabelMouseLeave = () => {
		setGuide((prev) => ({ ...prev, visible: false }));
	};
	const getFootprint = (comp) => {
		const W = Number(comp?.size?.width) || 0;
		const H = Number(comp?.size?.height) || 0;
		const x = Number(comp?.position?.x) || 0;
		const y = Number(comp?.position?.y) || 0;
		const rot = ((Math.round((Number(comp?.rotate) || 0) / 90) * 90) % 360 + 360) % 360;
		const swap = rot === 90 || rot === 270;
		const w = swap ? H : W;
		const h = swap ? W : H;
		return { x: x + (W - w) / 2, y: y + (H - h) / 2, w, h };
	};

	// (mx,my) model-point ke neeche aane wale saare components — array order me (aakhri =
	// sabse upar). Patli cheezein (line) ko thoda pad karte hain taaki easily hit ho.
	const componentsAtPoint = (mx, my) => (currentLabelComponents || []).filter((c) => {
		const fp = getFootprint(c);
		const px = fp.w < 8 ? (8 - fp.w) / 2 : 0;
		const py = fp.h < 8 ? (8 - fp.h) / 2 : 0;
		return mx >= fp.x - px && mx <= fp.x + fp.w + px
			&& my >= fp.y - py && my <= fp.y + fp.h + py;
	});

	const ALIGN_SNAP = 5;
	const computeAlign = (dragId, bx, by, bw, bh) => {
		const labelW = (Number(labelDetails?.size?.width) || 0) * MM_TO_PX;
		const labelH = (Number(labelDetails?.size?.height) || 0) * MM_TO_PX;
		const others = currentLabelComponents.filter((c) => c?.id !== dragId).map(getFootprint);
		const vTargets = [];
		const hTargets = [];
		others.forEach((t) => {
			vTargets.push({ pos: t.x, a: t.y, b: t.y + t.h });
			vTargets.push({ pos: t.x + t.w / 2, a: t.y, b: t.y + t.h });
			vTargets.push({ pos: t.x + t.w, a: t.y, b: t.y + t.h });
			hTargets.push({ pos: t.y, a: t.x, b: t.x + t.w });
			hTargets.push({ pos: t.y + t.h / 2, a: t.x, b: t.x + t.w });
			hTargets.push({ pos: t.y + t.h, a: t.x, b: t.x + t.w });
		});
		vTargets.push({ pos: 0, a: 0, b: labelH }, { pos: labelW / 2, a: 0, b: labelH }, { pos: labelW, a: 0, b: labelH });
		hTargets.push({ pos: 0, a: 0, b: labelW }, { pos: labelH / 2, a: 0, b: labelW }, { pos: labelH, a: 0, b: labelW });

		const dragV = [bx, bx + bw / 2, bx + bw];
		const dragH = [by, by + bh / 2, by + bh];
		let bestV = null;
		dragV.forEach((dv) => vTargets.forEach((t) => {
			const d = Math.abs(dv - t.pos);
			if (d <= ALIGN_SNAP && (!bestV || d < bestV.d)) {
				bestV = { d, snapX: bx + (t.pos - dv), line: { pos: t.pos, a: Math.min(t.a, by), b: Math.max(t.b, by + bh) } };
			}
		}));
		let bestH = null;
		dragH.forEach((dh) => hTargets.forEach((t) => {
			const d = Math.abs(dh - t.pos);
			if (d <= ALIGN_SNAP && (!bestH || d < bestH.d)) {
				bestH = { d, snapY: by + (t.pos - dh), line: { pos: t.pos, a: Math.min(t.a, bx), b: Math.max(t.b, bx + bw) } };
			}
		}));
		return {
			x: bestV ? bestV.snapX : bx,
			y: bestH ? bestH.snapY : by,
			v: bestV ? [bestV.line] : [],
			h: bestH ? [bestH.line] : [],
		};
	};
	const [customSize, setCustomSize] = useState(false);
	const [sizeFallbackUsed, setSizeFallbackUsed] = useState(false);
	const open = Boolean(anchorEl);
	const presetWidths = [...new Set([...LABEL_SIZE_PRESETS.map((p) => p.width), labelDetails.size.width])].sort((a, b) => a - b);
	const presetHeights = [...new Set([...LABEL_SIZE_PRESETS.map((p) => p.height), labelDetails.size.height])].sort((a, b) => a - b);

	useEffect(() => {
		const currLabel = location.state?.currLabel;
		if (!currLabel) return;
		const comps = Array.isArray(currLabel?.label) ? currLabel.label : [];
		const labelMeta = comps.find((c) => c?.name === 'label');

		const zpl = currLabel?.zplCode;
		let parsed = null;
		if (zpl && String(zpl).trim()) {
			try {
				parsed = parseZplToLabel(zpl, currLabel.size);
			} catch (e) {
				console.log(e);
			}
		}
		const parsedComps = parsed ? migrateLabelComponents(parsed.components || []) : [];
		setLabelDetails((prev) => ({
			...prev,
			id: currLabel.id,
			name: currLabel.name ?? '',
			size: (parsedComps.length ? parsed.size : null) ?? currLabel.size ?? prev.size,
			labelType: currLabel.type ?? prev.labelType,
			printCount: currLabel.printCount ?? 1,
			dpmm: labelMeta?.dpmm ?? prev.dpmm,
			borderStyle: labelMeta?.style?.borderStyle ?? prev.borderStyle,
			thickness: labelMeta?.style?.thickness ?? prev.thickness,
			borderColor: labelMeta?.style?.borderColor ?? prev.borderColor,
			lineStyle: labelMeta?.style?.lineStyle ?? prev.lineStyle,
		}));
		setSizeFallbackUsed(!!(parsedComps.length && parsed && !parsed.sizeFromZpl));
		// Initial load history me nahi jaana chahiye — warna undo se label khali ho jaata.
		skipHistoryRef.current = true;
		if (parsedComps.length) {
			setCurrentLabelComponents(parsedComps);
			return;
		}
		setCurrentLabelComponents(migrateLabelComponents(comps.filter((c) => c?.name !== 'label')));
	}, []);


	const centerCardInView = (z) => {
		const el = canvasScrollRef.current;
		if (!el) return;
		const cardCenterX = (labelDetails.position.x + (labelDetails.size.width * MM_TO_PX) / 2) * z;
		const cardCenterY = (labelDetails.position.y + (labelDetails.size.height * MM_TO_PX) / 2) * z;
		el.scrollTo({
			left: cardCenterX - el.clientWidth / 2,
			top: cardCenterY - el.clientHeight / 2,
			behavior: 'smooth',
		});
	};

	const handleZoom = (dir) => {
		const next = dir > 0
			? Math.min(4, Math.round((zoom + 0.25) * 100) / 100)
			: Math.max(0.5, Math.round((zoom - 0.25) * 100) / 100);
		setZoom(next);
		requestAnimationFrame(() => centerCardInView(next));
	};

	useEffect(() => {
		const el = canvasScrollRef.current;
		if (!el) return undefined;
		const onWheel = (e) => {
			if (!(e.ctrlKey || e.metaKey)) return;
			e.preventDefault();
			setZoom((z) => (e.deltaY < 0
				? Math.min(4, Math.round((z + 0.1) * 100) / 100)
				: Math.max(0.5, Math.round((z - 0.1) * 100) / 100)));
		};
		el.addEventListener('wheel', onWheel, { passive: false });
		return () => el.removeEventListener('wheel', onWheel);
	}, []);

	const labelTypeOptions = labelGeneratorConfig?.namingOfMachineType || {};
	const labelTypeLabel = labelTypeOptions[labelDetails.labelType] || labelDetails.labelType;
	const labelTypeData = Object.entries(labelTypeOptions).map(([value, label]) => ({ label, value }));

	const handleClick = (event) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = () => {
		setAnchorEl(null);
	};
	const handleSizeChange = (key, value) => {
		setLabelDetails((prev) => ({ ...prev, size: { ...prev.size, [key]: value } }));
	};

	const handleBack = () => {
		history.push(`${urls.EXTENSION}${urls.LABEL_GENERATOR_LIST}`);
	};

	const handlePreview = async () => {
		if (previewMode) {
			setPreviewMode(false);
			setZoom(1);
			if (zplPreview.url) URL.revokeObjectURL(zplPreview.url);
			setZplPreview({ loading: false, url: '', error: '' });
			return;
		}
		if (!labelDetails?.name?.trim()) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Please enter a label name',
			});
			return;
		}
		setSelectedComponent({ name: 'label' });
		setEditingId(null);
		setZoom(0.7);
		setPreviewMode(true);
		setZplPreview({ loading: true, url: '', error: '' });
		try {
			const zpl = await generateZpl(labelDetails, currentLabelComponents);
			const dpmm = Number(labelDetails?.dpmm) || 8;
			const wIn = Math.max(0.1, (Number(labelDetails?.size?.width) || 0) / 25.4).toFixed(2);
			const hIn = Math.max(0.1, (Number(labelDetails?.size?.height) || 0) / 25.4).toFixed(2);
			const res = await fetch(`https://api.labelary.com/v1/printers/${dpmm}dpmm/labels/${wIn}x${hIn}/0/`, {
				method: 'POST',
				headers: { Accept: 'image/png', 'Content-Type': 'application/x-www-form-urlencoded' },
				body: zpl,
			});
			if (!res.ok) {
				const detail = await res.text().catch(() => '');
				throw new Error(detail || `Server returned status ${res.status}`);
			}
			const url = URL.createObjectURL(await res.blob());
			setZplPreview({ loading: false, url, error: '' });
		} catch (e) {
			setZplPreview({ loading: false, url: '', error: e?.message || 'Unable to render preview. Please try again.' });
		}
	};

	const handleSave = async () => {
		if (!labelDetails?.name?.trim()) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Please enter a label name',
			});
			return;
		}
		const zplCode = await generateZpl(labelDetails, currentLabelComponents);
		const labelMeta = {
			name: 'label',
			dpmm: Number(labelDetails.dpmm ?? 8),
			style: {
				borderStyle: labelDetails.borderStyle,
				thickness: labelDetails.thickness,
				borderColor: labelDetails.borderColor,
				lineStyle: labelDetails.lineStyle,
			},
		};
		const payload = {
			...(labelDetails.id ? { id: labelDetails.id } : {}),
			name: labelDetails.name,
			size: labelDetails.size,
			type: labelDetails.labelType,
			printCount: Number(labelDetails.printCount ?? 1),
			label: [labelMeta, ...currentLabelComponents],
			zplCode,
		};
		try {
			const res = await labelGeneratorServices.createLabel(payload);
			if (res?.statusCode === 200) {
				notificationServices.generateNotification({
					type: 'success',
					message: 'Label saved',
				});
				history.push(`${urls.EXTENSION}${urls.LABEL_GENERATOR_LIST}`);
			} else {
				notificationServices.generateNotification({
					type: 'error',
					message: res?.message || 'Unable to save label',
				});
			}
		} catch (e) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Unable to save label, please connect with UY Team',
			});
		}
	};

	const handleCurrentLabelComponent = (id, updates) => {
		setCurrentLabelComponents((prev) => prev.map((c) =>
			c.id === id ? { ...c, ...updates } : c
		));
	};

	const handleToolStateChange = useCallback((key, value) => {
		setToolState((prev) => withBorderDefaults(prev, key, value));
		if (selectedComponent?.parent === 'table') {
			const { tableId, cellPos } = selectedComponent;
			setCurrentLabelComponents((prev) => prev.map((comp) => {
				if (comp?.id !== tableId) return comp;
				const baseCells = buildCells(comp?.rows, comp?.cols, comp?.cells);
				const cells = baseCells.map((row, ri) => row.map((cell, ci) =>
					(ri === cellPos?.r && ci === cellPos?.c)
						? { ...cell, [key]: value }
						: cell));
				return { ...comp, cells };
			}));
			return;
		}
		if (selectedComponent?.name === 'label') {
			setLabelDetails((prev) => withBorderDefaults(prev, key, value));
			return;
		}
		if (selectedComponent?.id) {
			setCurrentLabelComponents((prev) => prev.map((c) =>
				c.id === selectedComponent.id ? withBorderDefaults(c, key, value) : c
			));
		}
	}, [selectedComponent, currentLabelComponents, handleCurrentLabelComponent]);

	const handleInlineFormat = (cmd, stateKey) => {
		const active = document.activeElement;
		if (active && active.isContentEditable) {
			document.execCommand(cmd, false, null);
			return;
		}
		handleToolStateChange(stateKey, !toolState[stateKey]);
	};

	useEffect(() => {
		const onSelChange = () => {
			const sel = document.getSelection();
			if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
			const range = sel.getRangeAt(0);
			let node = range.commonAncestorContainer;
			if (node.nodeType === 3) node = node.parentElement;
			const editable = node?.closest?.('[contenteditable="true"]');
			if (editable) {
				savedSelectionRef.current = { range: range.cloneRange(), editable };
			}
		};
		document.addEventListener('selectionchange', onSelChange);
		return () => document.removeEventListener('selectionchange', onSelChange);
	}, []);

	const applyInlineStyle = (styleProp, value) => {
		const saved = savedSelectionRef.current;
		if (!saved) return false;
		const { range, editable } = saved;
		if (!editable || !document.body.contains(editable) || range.collapsed) return false;
		if (!editable.contains(range.commonAncestorContainer)) return false;

		let container = range.commonAncestorContainer;
		if (container.nodeType === 3) container = container.parentElement;
		let span;
		if (container && container.tagName === 'SPAN' && container.textContent === range.toString()) {
			container.style[styleProp] = value;
			span = container;
		} else {
			span = document.createElement('span');
			span.style[styleProp] = value;
			try {
				span.appendChild(range.extractContents());
				range.insertNode(span);
			} catch (e) {
				return false;
			}
		}
		const newRange = document.createRange();
		newRange.selectNodeContents(span);
		savedSelectionRef.current = { range: newRange, editable };
		editable.dispatchEvent(new Event('input', { bubbles: true }));
		return true;
	};
	const handleFontChange = (styleProp, cssValue, stateKey, stateValue) => {
		setToolState((prev) => ({ ...prev, [stateKey]: stateValue }));
		if (applyInlineStyle(styleProp, cssValue)) return;
		handleToolStateChange(stateKey, stateValue);
	};

	const applyInlineHighlight = () => {
		const saved = savedSelectionRef.current;
		if (!saved) return false;
		const { range, editable } = saved;
		if (!editable || !document.body.contains(editable) || range.collapsed) return false;
		if (!editable.contains(range.commonAncestorContainer)) return false;

		let container = range.commonAncestorContainer;
		if (container.nodeType === 3) container = container.parentElement;
		let span;
		if (container && container.tagName === 'SPAN' && container.textContent === range.toString()) {
			span = container;
		} else {
			span = document.createElement('span');
			try {
				span.appendChild(range.extractContents());
				range.insertNode(span);
			} catch (e) {
				return false;
			}
		}
		span.style.backgroundColor = 'rgb(0, 0, 0)';
		span.style.color = 'rgb(255, 255, 255)';
		const newRange = document.createRange();
		newRange.selectNodeContents(span);
		savedSelectionRef.current = { range: newRange, editable };
		editable.dispatchEvent(new Event('input', { bubbles: true }));
		return true;
	};

	const handleHighlight = () => {
		const active = document.activeElement;
		if (active && active.isContentEditable && applyInlineHighlight()) return;
		handleToolStateChange('isHighlight', !toolState.isHighlight);
	};

	const addComponent = (key, label) => {
		handleClose();
		if (key === 'text') {
			const data = {
				id: Date.now(),
				name: 'text',
				content: '',
				placeholder: '',
				fontSize: 16,
				lineHeight: 1.2,
				fontFamily: '0',
				textAlign: 'left',
				position: { x: 10, y: 10 },
				size: { width: 120, height: 28 },
				color: '#000000',
				isBold: false,
				isItalic: false,
				isUnderline: false,
				isHighlight: false,
				rotate: 0,
				borderRadius: 0,
				borderStyle: 'none',
				borderColor: '#000000',
				thickness: 1,
				selected: false,
			}
			setCurrentLabelComponents((prev) => [...prev, data])
			setSelectedComponent(data)
		} else if (key === 'staticText') {
			const data = {
				id: Date.now(),
				name: 'staticText',
				content: '',
				fontSize: 14,
				lineHeight: 1.2,
				fontFamily: '0',
				textAlign: 'left',
				position: { x: 10, y: 10 },
				size: { width: 160, height: 60 },
				color: '#000000',
				isBold: false,
				isItalic: false,
				isUnderline: false,
				isHighlight: false,
				rotate: 0,
				borderRadius: 0,
				borderStyle: 'none',
				borderColor: '#000000',
				thickness: 1,
				selected: false,
			}
			setCurrentLabelComponents((prev) => [...prev, data])
			setSelectedComponent(data)
		} else if (key === 'line') {
			const data = {
				id: Date.now(),
				name: 'line',
				position: { x: 10, y: 10 },
				size: { width: 120, height: 2 },
				color: '#000000',
				selected: false,
			}
			setCurrentLabelComponents((prev) => [...prev, data])
			setSelectedComponent(data)
		} else if (key === 'qrCode' || key === 'barcode') {
			const data = {
				id: Date.now(),
				name: key,
				src: key === 'qrCode' ? qrCodeImg : barCodeImg,
				position: { x: 10, y: 10 },
				size: key === 'qrCode' ? { width: 70, height: 70 } : { width: 140, height: 50 },
				rotate: 0,
				borderRadius: 0,
				borderStyle: 'none',
				borderColor: '#000000',
				thickness: 1,
				selected: false,
			}
			setCurrentLabelComponents((prev) => [...prev, data])
			setSelectedComponent(data)
		} else if (key === 'image') {
			const data = {
				id: Date.now(),
				name: 'image',
				src: '',
				file: null,
				position: { x: 10, y: 10 },
				size: { width: 160, height: 100 },
				rotate: 0,
				borderRadius: 0,
				borderStyle: 'none',
				borderColor: '#000000',
				thickness: 1,
				selected: false,
			}
			setCurrentLabelComponents((prev) => [...prev, data])
			setSelectedComponent(data)
		} else if (key === 'table') {
			const data = {
				id: Date.now(),
				name: 'table',
				position: { x: 10, y: 10 },
				size: { width: 160, height: 80 },
				rows: 2,
				cols: 2,
				color: '#000000',
				borderStyle: 'solid',
				borderColor: '#000000',
				thickness: 1,
				selected: false,
			}
			setCurrentLabelComponents((prev) => [...prev, data])
			setSelectedComponent(data)
		} else if (key === 'rectangle' || key === 'circle') {
			const data = {
				id: Date.now(),
				name: key,
				position: { x: 10, y: 10 },
				size: key === 'circle' ? { width: 80, height: 80 } : { width: 120, height: 70 },
				color: '#000000',     // fill colour (used when fill = true)
				fill: false,          // outline by default
				rotate: 0,
				borderStyle: 'solid',
				borderColor: '#000000',
				thickness: 2,
				borderRadius: 0,
				selected: false,
			}
			setCurrentLabelComponents((prev) => [...prev, data])
			setSelectedComponent(data)
		}
	};

	const handleSelectedComponent = (comp) => {
		let target = comp;
		// Overlapping components: click point ke neeche agar ek se zyada component hain to
		// har click par stack me neeche wale ko select karo (cycle) — taaki upar wale ke
		// neeche dabe component bhi select ho sakein.
		if (comp?.id) {
			const { x, y } = clickPtRef.current;
			const stack = componentsAtPoint(x, y);
			if (stack.length > 1) {
				const curIdx = stack.findIndex((c) => c.id === selectedComponent?.id);
				target = curIdx === -1
					? stack[stack.length - 1]                       // pehla click -> sabse upar wala
					: stack[(curIdx - 1 + stack.length) % stack.length]; // dobara click -> neeche wala
			}
		}
		// Doosre component pe switch karte hi purani saved text-selection invalidate
		// karo, warna toolbar se font size badalne par wo PICHLE editable pe lag jaata hai.
		if (target?.id !== selectedComponent?.id) {
			savedSelectionRef.current = null;
		}
		setSelectedComponent(target);
		if (target?.id !== editingId) setEditingId(null);
	};

	const handleTableCellSelect = (tableId, r, c, cell) => {
		savedSelectionRef.current = null;
		setSelectedComponent({
			name: 'text',
			parent: 'table',
			tableId,
			cellPos: { r, c },
		});
		setToolState((prev) => ({
			...prev,
			fontFamily: cell?.fontFamily ?? prev.fontFamily,
			fontSize: cell?.fontSize ?? prev.fontSize,
			textAlign: cell?.textAlign ?? 'left',
			color: cell?.color ?? prev.color,
			isBold: cell?.isBold ?? false,
			isItalic: cell?.isItalic ?? false,
			isUnderline: cell?.isUnderline ?? false,
			isHighlight: cell?.isHighlight ?? false,
			rotate: cell?.rotate ?? 0,
			borderStyle: cell?.borderStyle ?? 'none',
			thickness: cell?.thickness ?? prev.thickness,
			borderRadius: cell?.borderRadius ?? 0,
		}));
	};

	useEffect(() => {
		if (!selectedComponent?.id) return;
		const comp = currentLabelComponents.find((c) => c.id === selectedComponent.id);
		if (!comp) return;
		setToolState((prev) => ({
			...prev,
			borderColor: comp.borderColor ?? prev.borderColor,
			thickness: comp.thickness ?? prev.thickness,
			borderStyle: comp.borderStyle ?? prev.borderStyle,
			lineStyle: comp.lineStyle ?? prev.lineStyle,
			fontFamily: comp.fontFamily ?? prev.fontFamily,
			fontSize: comp.fontSize ?? prev.fontSize,
			lineHeight: comp.lineHeight ?? prev.lineHeight,
			textAlign: comp.textAlign ?? 'left',
			rotate: comp.rotate ?? prev.rotate,
			borderRadius: comp.borderRadius ?? 0,
			isBold: comp.isBold ?? false,
			isItalic: comp.isItalic ?? false,
			isUnderline: comp.isUnderline ?? false,
			isHighlight: comp.isHighlight ?? false,
			fill: comp.fill ?? false,
		}));
	}, [selectedComponent?.id]);

	useEffect(() => {
		const handleKeyDown = (event) => {
			const active = document.activeElement;
			const isTyping = active && (active.isContentEditable || active.tagName === 'INPUT' || active.tagName === 'TEXTAREA');
			if (isTyping || previewMode) return;
			const key = event.key?.toLowerCase();
			if ((event.ctrlKey || event.metaKey) && key === 'z') {
				event.preventDefault();
				if (event.shiftKey) handleRedo(); else handleUndo();
				return;
			}
			if ((event.ctrlKey || event.metaKey) && key === 'y') {
				event.preventDefault();
				handleRedo();
				return;
			}
			if (selectedComponent?.id && !selectedComponent?.parent && key === 'delete') {
				handleDeleteComponent();
			}
		};
		document.addEventListener("keydown", handleKeyDown);
		return () => {
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [selectedComponent, previewMode, handleUndo, handleRedo]);

	const placeholderOptions =
		labelGeneratorConfig?.possiblePlaceholderArray?.[labelDetails?.labelType] || [];

	const handleSelectPlaceholder = (value) => {
		handleCurrentLabelComponent(selectedComponent?.id, { content: value, placeholder: value });
		setPlaceholderAnchor(null);
	};

	const openImageCropModal = (name, file = null) => {
		dispatch(modalActions.toggleImageCrop(true, { name: String(name), file }));
	};

	const handleImageSelect = (e) => {
		const { fieldName } = inputHelper(e);
		const file = e.target.files?.[0];
		if (file) {
			handleCurrentLabelComponent(Number(fieldName), { file });
			openImageCropModal(fieldName, file);
		}
	};

	const handleImageInput = (fieldName, blob) => {
		handleCurrentLabelComponent(Number(fieldName), { src: blob });
	};

	// Delete ab seedha nahi hota — pehle confirmation modal khulta hai
	const handleDeleteComponent = () => {
		if (selectedComponent?.id) {
			setDeleteConfirmId(selectedComponent.id);
		}
	};

	const handleDeleteComponentById = (id) => {
		setDeleteConfirmId(id);
	};

	const confirmDeleteComponent = () => {
		if (deleteConfirmId != null) {
			setCurrentLabelComponents((prev) => prev.filter((c) => c.id !== deleteConfirmId));
			setSelectedComponent({ name: 'label' });
			setEditingId(null);
		}
		setDeleteConfirmId(null);
	};


	const renderSelectComponetsStyles = useCallback(() => {
		const activeStyleForComponent = selectedComponent?.parent === 'table'
			? tableCellActiveStyles
			: ActiveStyle[selectedComponent?.name];
		const isActive = activeStyleForComponent
			? activeStyleForComponent
			: { includes: () => true };
		const isDisabled = (...keys) =>
			activeStyleForComponent
				? !keys.some((key) => isActive.includes(key))
				: false;
		const selectedTableComp = selectedComponent?.name === 'table'
			? currentLabelComponents.find((c) => c.id === selectedComponent?.id)
			: null;
		const commitTableLayout = (r, c) => {
			if (!selectedComponent?.id) return;
			handleCurrentLabelComponent(selectedComponent.id, { rows: r, cols: c, colWidths: undefined, rowHeights: undefined });
			setTableLayoutAnchor(null);
			setHoverGrid({ rows: 0, cols: 0 });
		};
		return (
			<div className='flex items-end justify-between flex-nowrap w-full px-2 py-1'>
				{selectedComponent?.name === 'table' && (
					<div className='flex flex-col items-center gap-0.5'>
						<span className='text-[11px] font-medium text-gray-500 whitespace-nowrap'>Table</span>
						<button
							type='button'
							title='Table Layout'
							className='flex items-center justify-center h-9 px-3 border border-gray-300 rounded-md bg-white cursor-pointer text-gray-700 shrink-0 hover:bg-gray-50 text-[13px] whitespace-nowrap'
							onClick={(e) => setTableLayoutAnchor(e.currentTarget)}
						>
							Layout ({selectedTableComp?.rows || 0} × {selectedTableComp?.cols || 0})
						</button>
						<Menu
							anchorEl={tableLayoutAnchor}
							open={Boolean(tableLayoutAnchor)}
							onClose={() => { setTableLayoutAnchor(null); setHoverGrid({ rows: 0, cols: 0 }); }}
							anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
						>
							<div style={{ padding: 12 }}>
								<div
									style={{ display: 'grid', gridTemplateColumns: `repeat(${GRID_MAX_COLS}, 22px)`, gap: 4 }}
									onMouseLeave={() => setHoverGrid({ rows: 0, cols: 0 })}
								>
									{Array.from({ length: GRID_MAX_ROWS }).map((_, r) => (
										Array.from({ length: GRID_MAX_COLS }).map((__, c) => {
											const activeR = hoverGrid.rows || selectedTableComp?.rows || 0;
											const activeC = hoverGrid.cols || selectedTableComp?.cols || 0;
											const isOn = r < activeR && c < activeC;
											return (
												<div
													key={`${r}-${c}`}
													onMouseEnter={() => setHoverGrid({ rows: r + 1, cols: c + 1 })}
													onClick={() => commitTableLayout(r + 1, c + 1)}
													style={{
														width: 22,
														height: 22,
														border: '1px solid #9ca3af',
														borderRadius: 3,
														cursor: 'pointer',
														background: isOn ? '#4a70fb' : '#fff',
													}}
												/>
											);
										})
									))}
								</div>
								<div style={{ marginTop: 8, textAlign: 'center', fontSize: 13, color: '#374151' }}>
									{(hoverGrid.rows || selectedTableComp?.rows || 0)} × {(hoverGrid.cols || selectedTableComp?.cols || 0)}
								</div>
							</div>
						</Menu>
					</div>
				)}
				{/* Border Style */}
				<div className={`flex flex-col items-center gap-0.5 ${isDisabled('borderStyle') && 'opacity-50 pointer-events-none'
					}`}>
					<span className='text-[11px] font-medium text-gray-500 whitespace-nowrap'>Border Style</span>
					<Select
						value={toolState.borderStyle}
						onChange={(e) => handleToolStateChange('borderStyle', e.target.value)}
						renderValue={(value) => renderLinePreview(value, 56)}
						sx={{ ...muiSelectSx, width: 110 }}
						disabled={!isActive.includes('borderStyle')}
					>
						{lineStyleOptions.map((opt) => (
							<MenuItem key={opt.value} value={opt.value}>
								{renderLinePreview(opt.value, 90)}
							</MenuItem>
						))}
					</Select>
				</div>

				{/* Border Width */}
				<div className={`flex flex-col items-center gap-0.5 ${isDisabled('thickness') && 'opacity-50 pointer-events-none'
					}`}>
					<span className='text-[11px] font-medium text-gray-500 whitespace-nowrap'>Thickness</span>
					<div className='flex items-center h-9 border border-gray-300 rounded-md bg-white overflow-hidden'>
						<button
							type='button'
							title='Decrease'
							onClick={() => handleToolStateChange('thickness', Math.max(0, toolState.thickness - 1))}
							className='w-7 h-9 flex items-center justify-center text-gray-600 hover:bg-gray-50 cursor-pointer'
						>
							−
						</button>
						<span className='w-10 text-center text-[13px] text-gray-800 tabular-nums select-none'>{toolState.thickness} px</span>
						<button
							type='button'
							title='Increase'
							onClick={() => handleToolStateChange('thickness', Math.max(0, toolState.thickness + 1))}
							className='w-7 h-9 flex items-center justify-center text-gray-600 hover:bg-gray-50 cursor-pointer'
						>
							+
						</button>
					</div>
				</div>

				{/* Line */}
				<div className={`flex flex-col items-center gap-0.5 ${isDisabled('lineStyle') && 'opacity-50 pointer-events-none'
					}`}>
					<span className='text-[11px] font-medium text-gray-500 whitespace-nowrap'>Line</span>
					<Select
						value={toolState.lineStyle}
						onChange={(e) => handleToolStateChange('lineStyle', e.target.value)}
						renderValue={(value) => renderLinePreview(value, 56)}
						sx={{ ...muiSelectSx, width: 110 }}
						disabled={isDisabled('lineStyle')}
					>
						{lineStyleOptions.map((opt) => (
							<MenuItem key={opt.value} value={opt.value}>
								{renderLinePreview(opt.value, 90)}
							</MenuItem>
						))}
					</Select>
				</div>

				{/* Font: sirf default scalable font ('0') use hota hai - dropdown ki jagah static text */}
				<div className={`flex flex-col items-center gap-0.5 ${isDisabled('fontFamily') && 'opacity-50 pointer-events-none'
					}`}>
					<span className='text-[11px] font-medium text-gray-500 whitespace-nowrap'>Text Style</span>
					<div className='flex items-center justify-center h-9 px-3 border border-gray-300 rounded-md bg-gray-50 text-[13px] text-gray-700 whitespace-nowrap select-none'>
						Default (scalable)
					</div>
				</div>
				{/* Font Size */}
				<div className={`flex flex-col items-center gap-0.5 ${isDisabled('fontSize') && 'opacity-50 pointer-events-none'
					}`}>
					<span className='text-[11px] font-medium text-gray-500 whitespace-nowrap'>Text Size</span>
					<input
						type='number'
						min='1'
						value={toolState.fontSize}
						onChange={(e) => {
							const v = e.target.value;
							if (v === '') { handleToolStateChange('fontSize', ''); return; }
							handleFontChange('fontSize', `${Number(v)}px`, 'fontSize', Number(v));
						}}
						disabled={isDisabled('fontSize')}
						className='w-14 h-9 border border-gray-300 rounded-md text-center text-[13px] px-2 outline-none box-border'
					/>
				</div>

				{/* Line Spacing */}
				<div className={`flex flex-col items-center gap-0.5 ${isDisabled('lineHeight') && 'opacity-50 pointer-events-none'
					}`}>
					<span className='text-[11px] font-medium text-gray-500 whitespace-nowrap'>Line Spacing</span>
					<input
						type='number'
						min='0.5'
						step='0.1'
						value={toolState.lineHeight}
						onChange={(e) => {
							const v = e.target.value;
							if (v === '') { handleToolStateChange('lineHeight', ''); return; }
							handleFontChange('lineHeight', `${Number(v)}`, 'lineHeight', Number(v));
						}}
						disabled={isDisabled('lineHeight')}
						className='w-14 h-9 border border-gray-300 rounded-md text-center text-[13px] px-2 outline-none box-border'
					/>
				</div>
				<div className={`flex flex-col items-center gap-0.5 ${isDisabled('textAlign') && 'opacity-50 pointer-events-none'
					}`}>
					<span className='text-[11px] font-medium text-gray-500 whitespace-nowrap'>Align</span>
					<Select
						value={toolState.textAlign || 'left'}
						onChange={(e) => handleToolStateChange('textAlign', e.target.value)}
						renderValue={(value) => {
							const opt = alignOptions.find((o) => o.value === value) || alignOptions[0];
							return (
								<span className='flex items-center gap-1.5'>
									<opt.Icon fontSize='small' />
									<span className='text-[13px]'>{opt.label}</span>
								</span>
							);
						}}
						sx={{ ...muiSelectSx, width: 110 }}
						disabled={isDisabled('textAlign')}
					>
						{alignOptions.map(({ value, label, Icon }) => (
							<MenuItem key={value} value={value}>
								<span className='flex items-center gap-1.5'>
									<Icon fontSize='small' />
									<span className='text-[13px]'>{label}</span>
								</span>
							</MenuItem>
						))}
					</Select>
				</div>
				<div className={`flex flex-col items-center gap-0.5 ${isDisabled('isBold') && 'opacity-50 pointer-events-none'
					}`}>
					<span className='text-[11px] font-medium text-gray-500 whitespace-nowrap'>Text</span>
					<div className='flex items-center gap-1'>
						<button
							type='button'
							title='Bold'
							className={`flex items-center justify-center h-9 w-9 rounded-md cursor-pointer shrink-0 ${toolState.isBold
								? 'border-2 border-[#4a70fb] bg-blue-50 text-[#4a70fb]'
								: 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
								}`}
							onMouseDown={(e) => e.preventDefault()}
							onClick={() => handleInlineFormat('bold', 'isBold')}
						>
							<FormatBold fontSize='small' />
						</button>
					</div>
				</div>

				{/* Highlight: poore field/cell ko reverse-print (black box + white text) karta hai.
					ZPL me ^GB filled box + ^FR reverse field se print hota hai. */}
				<div className={`flex flex-col items-center gap-0.5 ${isDisabled('isHighlight') && 'opacity-50 pointer-events-none'
					}`}>
					<span className='text-[11px] font-medium text-gray-500 whitespace-nowrap'>Highlight</span>
					<div className='flex items-center gap-1'>
						<button
							type='button'
							title='Highlight (reverse print)'
							className={`flex items-center justify-center h-9 w-9 rounded-md cursor-pointer shrink-0 ${toolState.isHighlight
								? 'border-2 border-[#4a70fb] bg-blue-50 text-[#4a70fb]'
								: 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
								}`}
							onMouseDown={(e) => e.preventDefault()}
							onClick={handleHighlight}
						>
							<Highlight fontSize='small' />
						</button>
					</div>
				</div>
				{/* Fill: shape ko solid bhar deta hai (ZPL me ^GB/^GC ka thickness = full side). */}
				<div className={`flex flex-col items-center gap-0.5 ${isDisabled('fill') && 'opacity-50 pointer-events-none'
					}`}>
					<span className='text-[11px] font-medium text-gray-500 whitespace-nowrap'>Fill</span>
					<button
						type='button'
						title='Fill shape'
						className={`flex items-center justify-center h-9 w-9 rounded-md cursor-pointer shrink-0 ${toolState.fill
							? 'border-2 border-[#4a70fb] bg-blue-50 text-[#4a70fb]'
							: 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
							}`}
						onClick={() => handleToolStateChange('fill', !toolState.fill)}
					>
						<div className='w-4 h-4 rounded-sm' style={{ background: toolState.fill ? 'currentColor' : 'transparent', border: '1.5px solid currentColor' }} />
					</button>
				</div>
				<div className={`flex flex-col items-center gap-0.5 ${isDisabled('rotate') && 'opacity-50 pointer-events-none'
					}`}>
					<span className='text-[11px] font-medium text-gray-500 whitespace-nowrap'>Box Rotation</span>
					{(() => {
						const curAngle = ((Math.round((Number(toolState.rotate) || 0) / 90) * 90) % 360 + 360) % 360;
						return (
							<div className='flex items-center gap-1'>
								<button
									type='button'
									title='Rotate (clockwise, +90°)'
									className='flex items-center justify-center h-9 w-9 border border-gray-300 rounded-md bg-white cursor-pointer text-gray-700 shrink-0 hover:bg-gray-50'
									onClick={() => handleToolStateChange('rotate', (curAngle + 90) % 360)}
								>
									<RotateRight fontSize='small' />
								</button>
								<span className='w-12 h-9 flex items-center justify-center border border-gray-300 rounded-md bg-gray-50 text-[13px] text-gray-800 tabular-nums select-none'>
									{curAngle}°
								</span>
							</div>
						);
					})()}
				</div>

				{/* Border Radius */}
				<div className={`flex flex-col items-center gap-0.5 ${isDisabled('borderRadius') && 'opacity-50 pointer-events-none'
					}`}>
					<span className='text-[11px] font-medium text-gray-500 whitespace-nowrap'>Box Radius</span>
					<div className='flex items-center h-9 border border-gray-300 rounded-md bg-white overflow-hidden'>
						<button
							type='button'
							title='Decrease'
							onClick={() => handleToolStateChange('borderRadius', Math.max(0, (toolState.borderRadius || 0) - 1))}
							className='w-7 h-9 flex items-center justify-center text-gray-600 hover:bg-gray-50 cursor-pointer'
						>
							−
						</button>
						<span className='w-10 text-center text-[13px] text-gray-800 tabular-nums select-none'>{toolState.borderRadius || 0} px</span>
						<button
							type='button'
							title='Increase'
							onClick={() => handleToolStateChange('borderRadius', Math.max(0, (toolState.borderRadius || 0) + 1))}
							className='w-7 h-9 flex items-center justify-center text-gray-600 hover:bg-gray-50 cursor-pointer'
						>
							+
						</button>
					</div>
				</div>
			</div>
		);
	}, [ActiveStyle,
		selectedComponent,
		currentLabelComponents,
		tableLayoutAnchor,
		hoverGrid,
		handleToolStateChange,
		toolState?.thickness,
		toolState?.fontSize,
		toolState?.isBold,
		toolState?.isItalic,
		toolState?.isUnderline,
		toolState?.isHighlight,
		toolState?.fill,
		toolState?.rotate,
		toolState?.borderRadius,
		toolState?.textAlign,
		toolState?.lineStyle,
		toolState?.fontFamily,
		toolState?.color,
		toolState?.borderColor,
		labelDetails?.borderStyle,
	])

	const ctrlIconSize = (comp) => {
		const minDim = Math.min(Number(comp?.size?.width) || 999, Number(comp?.size?.height) || 999);
		return Math.max(14, Math.min(26, Math.round(minDim * 0.32)));
	};

	const renderComponent = (labelComp) => {
		const cs = ctrlIconSize(labelComp);
		const isSelected = selectedComponent?.id === labelComp?.id;
		const isHighlight = !!labelComp?.isHighlight;
		const userBorder = labelComp?.borderStyle && labelComp?.borderStyle !== 'none'
			? `${labelComp?.thickness || 1}px ${labelComp?.borderStyle} ${labelComp?.borderColor || '#000000'}`
			: '1px dashed #c7c7c7';
		const textStyle = {
			fontSize: Number(labelComp?.fontSize) || 16,
			fontFamily: labelComp?.fontFamily,
			color: isHighlight ? '#ffffff' : labelComp?.color,
			fontWeight: labelComp?.isBold ? 'bold' : 'normal',
			fontStyle: labelComp?.isItalic ? 'italic' : 'normal',
			lineHeight: labelComp?.lineHeight || 'normal',
			textAlign: labelComp?.textAlign || 'left',
		};
		const boxStyle = {
			// rotation ab Rnd ke andar wrapper handle karta hai (footprint box ke saath),
			// isliye yahan transform mat lagao warna double-rotate ho jaayega.
			border: userBorder,
			boxShadow: isSelected ? '0 0 0 2px #4a70fb' : 'none',
			borderRadius: labelComp?.borderRadius ? `${labelComp?.borderRadius}px` : 0,
			...(isHighlight ? { background: '#000000' } : {}),
		};

		if (labelComp?.name === 'text') {
			// Sirf dropdown se field select hota hai - free text typing allowed nahi.
			// Select karne par chosen field ka text yahan dikh jaata hai (image jaisa flow).
			return (
				<div
					onClick={(e) => { e.stopPropagation(); handleSelectedComponent(labelComp); }}
					className='group relative w-full h-full flex items-center cursor-move px-0.5'
					style={boxStyle}
				>
					{isSelected && (
						<button
							type='button'
							className='label-text-input absolute -top-7 left-0 flex items-center gap-1 whitespace-nowrap rounded bg-[#4a70fb] px-2 py-0.5 text-[11px] text-white shadow-md'
							onClick={(e) => { e.stopPropagation(); setPlaceholderAnchor(e.currentTarget); }}
						>
							{labelComp?.placeholder || 'Select field'} ▾
						</button>
					)}
					{/* delete andar (rotate hone par content ke saath rehta hai) */}
					{!previewMode && (
						<button
							type='button'
							title='Delete'
							onClick={(e) => { e.stopPropagation(); handleDeleteComponentById(labelComp?.id); }}
							style={{ width: cs, height: cs }}
							className='label-text-input absolute top-1 right-1 z-30 hidden group-hover:flex items-center justify-center rounded-full bg-red-500 text-white shadow hover:bg-red-600'
						>
							<DeleteOutline sx={{ fontSize: Math.round(cs * 0.6) }} />
						</button>
					)}
					{labelComp?.content ? (
						<span
							className='block w-full p-0 m-0 truncate select-none'
							style={textStyle}
						>
							{labelComp?.content}
						</span>
					) : (
						<button
							type='button'
							title='Select field'
							onClick={(e) => {
								e.stopPropagation();
								if (previewMode) return;
								handleSelectedComponent(labelComp);
								setPlaceholderAnchor(e.currentTarget);
							}}
							className='flex items-center gap-1 w-full bg-transparent border-none outline-none p-0 m-0 text-left text-gray-400 truncate cursor-move'
							style={{ ...textStyle, color: '#9ca3af' }}
						>
							Select field ▾
						</button>
					)}
				</div>
			)
		}
		if (labelComp?.name === 'staticText') {
			const isEditing = editingId === labelComp?.id;
			return (
				<div
					onClick={(e) => { e.stopPropagation(); handleSelectedComponent(labelComp); }}
					onDoubleClick={(e) => { e.stopPropagation(); if (!previewMode) setEditingId(labelComp?.id); }}
					className='group relative w-full h-full cursor-move p-0.5'
					style={boxStyle}
				>
					{!previewMode && (
						<button
							type='button'
							title='Delete'
							onClick={(e) => { e.stopPropagation(); handleDeleteComponentById(labelComp?.id); }}
							style={{ width: cs, height: cs }}
							className='label-text-input absolute top-1 right-1 z-30 hidden group-hover:flex items-center justify-center rounded-full bg-red-500 text-white shadow hover:bg-red-600'
						>
							<DeleteOutline sx={{ fontSize: Math.round(cs * 0.6) }} />
						</button>
					)}
					<RichTextBox
						value={labelComp?.content ?? ''}
						editing={isEditing}
						autoFocus
						placeholder='enter text in this'
						onClick={(e) => { if (isEditing) e.stopPropagation(); }}
						onBlur={() => setEditingId(null)}
						onChange={(html) => handleCurrentLabelComponent(labelComp?.id, { content: html })}
						className={`${isEditing ? 'label-text-input' : ''} label-richtext ${isHighlight ? 'label-richtext-highlight' : ''} w-full h-full bg-transparent border-none outline-none p-0 m-0 overflow-hidden whitespace-pre-wrap break-words`}
						style={{
							...textStyle,
							pointerEvents: isEditing ? 'auto' : 'none',
							cursor: isEditing ? 'text' : 'move',
						}}
					/>
				</div>
			)
		}
		if (labelComp?.name === 'line') {
			return (
				<div
					onClick={(e) => { e.stopPropagation(); handleSelectedComponent(labelComp); }}
					className='group relative w-full h-full flex items-center cursor-move'
				>
					{/* Click ke liye chhota hit-area — line ke hisaab se (thickness + thoda), line pe
						hi centered. Bada band nahi taaki paas ke components (QR etc.) ke click na chura
						le. Genuine overlap ke liye click-cycling hai. */}
					<div
						onClick={(e) => { e.stopPropagation(); handleSelectedComponent(labelComp); }}
						style={{ position: 'absolute', left: 0, right: 0, top: '50%', transform: 'translateY(-50%)', height: Math.max(8, (Number(labelComp?.thickness) || 2) + 6), cursor: 'move', zIndex: 1 }}
					/>
					{/* delete andar (rotate hone par content ke saath rehta hai) */}
					{!previewMode && (
						<button
							type='button'
							title='Delete'
							onClick={(e) => { e.stopPropagation(); handleDeleteComponentById(labelComp?.id); }}
							style={{ width: 16, height: 16, top: '50%', right: -22, transform: 'translateY(-50%)' }}
							className='label-text-input absolute z-30 hidden group-hover:flex items-center justify-center rounded-full bg-red-500 text-white shadow hover:bg-red-600'
						>
							<DeleteOutline sx={{ fontSize: 10 }} />
						</button>
					)}
					<div
						style={{
							width: '100%',
							height: 0,
							borderTopWidth: `${labelComp?.thickness || 2}px`,
							borderTopStyle: labelComp?.lineStyle && labelComp?.lineStyle !== 'none' ? labelComp?.lineStyle : 'solid',
							// select hone par line khud blue ho jaati hai (Paint jaisa) — box/gap nahi.
							borderTopColor: isSelected ? '#4a70fb' : (labelComp?.color || labelComp?.borderColor || '#000000'),
							boxSizing: 'border-box',
						}}
					/>
				</div>
			)
		}
		if (labelComp?.name === 'rectangle' || labelComp?.name === 'circle') {
			const isCircle = labelComp?.name === 'circle';
			return (
				<div
					onClick={(e) => { e.stopPropagation(); handleSelectedComponent(labelComp); }}
					className='group relative w-full h-full cursor-move'
					style={{
						boxShadow: isSelected ? '0 0 0 2px #4a70fb' : 'none',
					}}
				>
					{!previewMode && (
						<button
							type='button'
							title='Delete'
							onClick={(e) => { e.stopPropagation(); handleDeleteComponentById(labelComp?.id); }}
							style={{ width: cs, height: cs }}
							className='label-text-input absolute top-1 right-1 z-30 hidden group-hover:flex items-center justify-center rounded-full bg-red-500 text-white shadow hover:bg-red-600'
						>
							<DeleteOutline sx={{ fontSize: Math.round(cs * 0.6) }} />
						</button>
					)}
					<div
						style={{
							width: '100%',
							height: '100%',
							boxSizing: 'border-box',
							border: `${labelComp?.thickness || 1}px ${labelComp?.borderStyle && labelComp?.borderStyle !== 'none' ? labelComp?.borderStyle : 'solid'} ${labelComp?.borderColor || '#000000'}`,
							borderRadius: isCircle ? '50%' : `${labelComp?.borderRadius || 0}px`,
							background: labelComp?.fill ? (labelComp?.color || '#000000') : 'transparent',
						}}
					/>
				</div>
			)
		}
		if (labelComp?.name === 'qrCode' || labelComp?.name === 'barcode') {
			return (
				<div
					onClick={(e) => { e.stopPropagation(); handleSelectedComponent(labelComp); }}
					className='group relative w-full h-full cursor-move'
					style={boxStyle}
				>
					<img
						src={labelComp?.src}
						alt={labelComp?.name}
						draggable={false}
						className='w-full h-full object-contain pointer-events-none select-none'
					/>
					{/* delete button andar (content ke saath rotate hota hai, floating jaisa door nahi) */}
					{!previewMode && (
						<button
							type='button'
							title='Delete'
							onClick={(e) => { e.stopPropagation(); handleDeleteComponentById(labelComp?.id); }}
							style={{ width: cs, height: cs }}
							className='label-text-input absolute top-1 right-1 z-10 hidden group-hover:flex items-center justify-center rounded bg-red-500 text-white shadow hover:bg-red-600'
						>
							<DeleteOutline sx={{ fontSize: Math.round(cs * 0.6) }} />
						</button>
					)}
				</div>
			)
		}
		if (labelComp?.name === 'image') {
			return (
				<div
					onClick={(e) => { e.stopPropagation(); handleSelectedComponent(labelComp); }}
					className='group relative w-full h-full cursor-move'
					style={boxStyle}
				>
					{labelComp?.src ? (
						<>
							<img
								src={labelComp?.src}
								alt='image'
								draggable={false}
								onClick={(e) => { e.stopPropagation(); handleSelectedComponent(labelComp); }}
								className='w-full h-full object-contain select-none'
								style={{ filter: 'grayscale(1) contrast(1.2)' }}
							/>
							<div
								onClick={(e) => e.stopPropagation()}
								className={`label-text-input absolute top-1 right-1 z-10 ${previewMode ? 'hidden' : 'hidden group-hover:flex'} items-center gap-1`}
							>
								{labelComp?.file && (
									<button
										type='button'
										title='Crop'
										onClick={(e) => { e.stopPropagation(); openImageCropModal(labelComp?.id, labelComp?.file); }}
										style={{ width: cs, height: cs }}
										className='flex items-center justify-center rounded bg-[#4a70fb] text-white shadow hover:bg-[#3a5fe0]'
									>
										<Crop sx={{ fontSize: Math.round(cs * 0.62) }} />
									</button>
								)}
								<label
									title='Change image'
									style={{ width: cs, height: cs }}
									className='flex items-center justify-center rounded bg-[#4a70fb] text-white shadow cursor-pointer hover:bg-[#3a5fe0]'
								>
									<SwapHoriz sx={{ fontSize: Math.round(cs * 0.66) }} />
									<input
										type='file'
										accept='image/*'
										name={String(labelComp?.id)}
										onChange={handleImageSelect}
										className='hidden'
									/>
								</label>
								<button
									type='button'
									title='Delete'
									onClick={(e) => { e.stopPropagation(); handleDeleteComponentById(labelComp?.id); }}
									style={{ width: cs, height: cs }}
									className='flex items-center justify-center rounded bg-red-500 text-white shadow hover:bg-red-600'
								>
									<DeleteOutline sx={{ fontSize: Math.round(cs * 0.62) }} />
								</button>
							</div>
						</>
					) : (
						<div className='label-text-input w-full h-full' onClick={(e) => e.stopPropagation()}>
							<DragDropImage
								name={String(labelComp?.id)}
								handleImageUpload={handleImageSelect}
							/>
						</div>
					)}
				</div>
			)
		}
		if (labelComp?.name === 'table') {
			return (
				<LabelTable
					rows={labelComp?.rows}
					cols={labelComp?.cols}
					cells={labelComp?.cells}
					colWidths={labelComp?.colWidths}
					rowHeights={labelComp?.rowHeights}
					scale={zoom}
					borderStyle={labelComp?.borderStyle}
					thickness={labelComp?.thickness}
					borderColor={labelComp?.borderColor}
					style={boxStyle}
					isEditing={editingId === labelComp?.id}
					selected={selectedComponent?.id === labelComp?.id}
					onClick={(e) => { e.stopPropagation(); handleSelectedComponent(labelComp); }}
					onDoubleClick={(e) => { e.stopPropagation(); if (!previewMode) setEditingId(labelComp?.id); }}
					onChange={(cells) => handleCurrentLabelComponent(labelComp?.id, { cells })}
					onLayoutChange={(layout) => handleCurrentLabelComponent(labelComp?.id, layout)}
					onCellSelect={(r, c, cell) => handleTableCellSelect(labelComp?.id, r, c, cell)}
					previewMode={previewMode}
					onDelete={() => handleDeleteComponentById(labelComp?.id)}
					deleteSize={cs}
				/>
			)
		}
	}

	return (
		<div className='w-full bg-gray-50'>
			{/* header */}
			<div className='flex items-center gap-4 px-6 py-1 bg-white border-b border-gray-200'>
				<div className='flex items-center gap-3 shrink-0'>
					<ArrowBackIcon
						className='cursor-pointer text-gray-700'
						onClick={handleBack}
					/>
					<h2 className='text-[22px] font-semibold text-gray-900 m-0'>Label Generator</h2>
				</div>
				<div className='flex items-center gap-4 text-[13.5px] text-gray-500 flex-1 min-w-0 overflow-x-auto whitespace-nowrap justify-end'>
					<span>Label Name: <span className='font-semibold text-gray-900'>{labelDetails.name || 'Untitled'}</span></span>
					<span>Label Size: <span className='font-semibold text-gray-900'>{labelDetails.size.width}×{labelDetails.size.height} mm</span></span>
					<span>Label Type: <span className='font-semibold text-gray-900'>{labelTypeLabel}</span></span>
					<span>Density: <span className='font-semibold text-gray-900'>{DENSITY_OPTIONS.find((d) => d.value === labelDetails.dpmm)?.label || `${labelDetails.dpmm} dpmm`}</span></span>
				</div>
				<div className='flex items-center gap-2 shrink-0'>
					<button
						type='button'
						onClick={handlePreview}
						className={`flex items-center justify-center gap-1 px-3 py-1.5 rounded-md border text-[13px] font-semibold whitespace-nowrap ${previewMode ? 'border-[#4a70fb] bg-[#eef2ff] text-[#4a70fb]' : 'border-gray-300 text-gray-800 hover:bg-gray-50'}`}
					>
						<VisibilityIcon fontSize="small" />
						{previewMode ? 'Edit' : 'Preview'}
					</button>
					<button
						type='button'
						onClick={handleSave}
						className='flex items-center justify-center gap-1 px-3 py-1.5 rounded-md bg-orange-400 hover:bg-orange-500 text-white text-[13px] font-semibold whitespace-nowrap'
					>
						<SaveIcon fontSize="small" />
						Save
					</button>
				</div>
			</div>
			{/* label Info */}
			{!previewMode && (
				<div className='flex items-center gap-4 flex-nowrap overflow-x-auto px-4 py-4 bg-white m-2 rounded-lg border border-gray-200 shadow-sm'>
					<>
						<div className='flex items-center gap-2 shrink-0'>
							<label className='text-[14px] font-medium text-gray-700 m-0 whitespace-nowrap'>Label Name:</label>
							<input
								type='text'
								value={labelDetails.name}
								onChange={(e) => setLabelDetails((prev) => ({ ...prev, name: e.target.value }))}
								className='w-40 border border-gray-300 rounded-md px-2.5 py-2 text-[14px] h-9 focus:outline-none focus:border-blue-500'
								placeholder='Enter label name'
							/>
						</div>

						<div className='flex items-center gap-1 shrink-0'>
							<label className='text-[14px] font-medium text-gray-700 m-0 whitespace-nowrap mr-1'>Label Size:</label>
							{/* Custom OFF: width/height dropdown (preset values). Custom ON: free number input */}
							{customSize ? (
								<input
									type='number'
									min='1'
									title='Custom width (mm)'
									value={labelDetails.size.width}
									onChange={(e) => handleSizeChange('width', Number(e.target.value))}
									className='w-16 border border-gray-300 rounded-md px-2.5 py-2 text-[14px] h-9 focus:outline-none focus:border-blue-500'
								/>
							) : (
								<Select
									value={labelDetails.size.width}
									onChange={(e) => handleSizeChange('width', Number(e.target.value))}
									renderValue={(v) => v}
									sx={{ ...muiSelectSx, width: 70 }}
								>
									{presetWidths.map((w) => (
										<MenuItem key={w} value={w}>{w}</MenuItem>
									))}
								</Select>
							)}
							<span className='text-gray-500'>×</span>
							{customSize ? (
								<input
									type='number'
									min='1'
									title='Custom height (mm)'
									value={labelDetails.size.height}
									onChange={(e) => handleSizeChange('height', Number(e.target.value))}
									className='w-16 border border-gray-300 rounded-md px-2.5 py-2 text-[14px] h-9 focus:outline-none focus:border-blue-500'
								/>
							) : (
								<Select
									value={labelDetails.size.height}
									onChange={(e) => handleSizeChange('height', Number(e.target.value))}
									renderValue={(v) => v}
									sx={{ ...muiSelectSx, width: 70 }}
								>
									{presetHeights.map((h) => (
										<MenuItem key={h} value={h}>{h}</MenuItem>
									))}
								</Select>
							)}
							<span className='text-[13px] text-gray-500'>mm</span>
							<SquarePen size={16} onClick={() => setCustomSize((v) => !v)}
								className={`cursor-pointer ${customSize
									? 'border-[#4a70fb] bg-blue-50 text-[#4a70fb]'
									: 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'}`} />
						</div>

						<div className='flex items-center gap-2 shrink-0'>
							<label className='text-[14px] font-medium text-gray-700 m-0 whitespace-nowrap'>Label Type:</label>
							<SelectPicker
								data={labelTypeData}
								value={labelDetails.labelType}
								onChange={(value) => setLabelDetails((prev) => ({ ...prev, labelType: value }))}
								cleanable={false}
								searchable={false}
								style={{ width: 190 }}
								className='[&_.rs-picker-toggle]:!h-9 [&_.rs-picker-toggle]:!flex [&_.rs-picker-toggle]:!items-center [&_.rs-picker-toggle]:!justify-start [&_.rs-picker-toggle]:!text-left [&_.rs-picker-toggle]:!text-[14px] [&_.rs-picker-toggle]:!py-0 [&_.rs-picker-toggle-value]:!text-left'
							/>
						</div>

						<div className='flex items-center gap-2 shrink-0'>
							<label className='text-[14px] font-medium text-gray-700 m-0 whitespace-nowrap'>Copies:</label>
							<input
								type='number'
								min='1'
								value={labelDetails.printCount}
								onChange={(e) => setLabelDetails((prev) => ({ ...prev, printCount: Number(e.target.value) }))}
								className='w-12 border border-gray-300 rounded-md px-2 py-2 text-[14px] h-9 focus:outline-none focus:border-blue-500'
							/>
						</div>

						<div className='flex items-center gap-2 shrink-0'>
							<label className='text-[14px] font-medium text-gray-700 m-0 whitespace-nowrap'>Density:</label>
							<SelectPicker
								data={DENSITY_OPTIONS}
								value={labelDetails.dpmm}
								onChange={(value) => setLabelDetails((prev) => ({ ...prev, dpmm: Number(value) }))}
								cleanable={false}
								searchable={false}
								style={{ width: 160 }}
								className='[&_.rs-picker-toggle]:!h-9 [&_.rs-picker-toggle]:!flex [&_.rs-picker-toggle]:!items-center [&_.rs-picker-toggle]:!justify-start [&_.rs-picker-toggle]:!text-left [&_.rs-picker-toggle]:!text-[14px] [&_.rs-picker-toggle]:!py-0 [&_.rs-picker-toggle-value]:!text-left'
							/>
						</div>

					</>
				</div>
			)}
			{/* compoents */}
			{!previewMode && (<div className='flex items-center gap-4 m-2 bg-white rounded-lg border border-gray-200 shadow-sm p-2'>
				<button
					type='button'
					className='flex items-center gap-2 px-5 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-900 text-[14px] font-semibold hover:bg-gray-50 shadow-sm whitespace-nowrap'
					onClick={handleClick}
				>
					<AddIcon fontSize='small' />
					Components
				</button>
				<Menu
					anchorEl={anchorEl}
					open={open}
					onClose={handleClose}
					anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
					transformOrigin={{ vertical: 'top', horizontal: 'left' }}
					PaperProps={{
						style: {
							marginTop: '8px',
							minWidth: '180px',
							borderRadius: '8px',
							boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
						}
					}}
				>
					{labelGeneratorConfig?.labelComponents?.map((comp) => (
						<MenuItem key={comp.key} onClick={() => addComponent(comp.key, comp.label)} className='!py-2.5 !px-4 hover:!bg-gray-50'>
							<div className='flex items-center gap-3 text-gray-700'>
								{comp.icon}
								<span className='text-[14px] font-medium'>{comp.label}</span>
							</div>
						</MenuItem>
					))}
				</Menu>
				<div className='self-stretch w-px bg-gray-300' />
				<div className='flex-1 min-h-[44px] rounded-lg bg-white border border-gray-200'>
					{renderSelectComponetsStyles()}
				</div>
			</div>
			)}
			{/* canvas */}
			<div ref={canvasScrollRef} className={`${previewMode ? 'h-[calc(100vh-150px)] overflow-hidden' : 'h-[calc(100vh-340px)] overflow-auto'} w-full relative`}>
				<div className='sticky top-2 left-2 z-10 w-fit flex items-center gap-0.5 h-9 rounded-md border border-[#4a70fb] bg-[#eef2ff] px-1 shadow-sm'>
					<button
						type='button'
						title='Zoom out'
						onClick={() => handleZoom(-1)}
						className='w-7 h-7 flex items-center justify-center rounded text-[#4a70fb] hover:bg-[#4a70fb] hover:text-white'
					>
						<ZoomOut fontSize='small' />
					</button>
					<span className='w-9 text-center text-[11px] font-semibold text-[#4a70fb] tabular-nums select-none'>{Math.round(zoom * 100)}%</span>
					<button
						type='button'
						title='Zoom in'
						onClick={() => handleZoom(1)}
						className='w-7 h-7 flex items-center justify-center rounded text-[#4a70fb] hover:bg-[#4a70fb] hover:text-white'
					>
						<ZoomIn fontSize='small' />
					</button>
					{!previewMode && (
						<>
							<div className='self-stretch w-px bg-[#4a70fb] opacity-30 mx-0.5' />
							<button
								type='button'
								title='Undo (Ctrl+Z)'
								onClick={handleUndo}
								disabled={!histLen.past}
								className={`w-7 h-7 flex items-center justify-center rounded ${histLen.past
									? 'text-[#4a70fb] hover:bg-[#4a70fb] hover:text-white cursor-pointer'
									: 'text-gray-300 cursor-not-allowed'}`}
							>
								<Undo fontSize='small' />
							</button>
							<button
								type='button'
								title='Redo (Ctrl+Y)'
								onClick={handleRedo}
								disabled={!histLen.future}
								className={`w-7 h-7 flex items-center justify-center rounded ${histLen.future
									? 'text-[#4a70fb] hover:bg-[#4a70fb] hover:text-white cursor-pointer'
									: 'text-gray-300 cursor-not-allowed'}`}
							>
								<Redo fontSize='small' />
							</button>
						</>
					)}
				</div>
				{previewMode ? (
					<div className='w-full h-full flex items-center justify-center overflow-hidden p-4'>
						{zplPreview.loading && (
							<div className='flex flex-col items-center gap-2 text-gray-500 text-sm'>
								<div className='w-6 h-6 border-2 border-gray-300 border-t-[#4a70fb] rounded-full animate-spin' />
								Rendering label…
							</div>
						)}
						{!zplPreview.loading && zplPreview.error && (
							<div className='flex flex-col items-center gap-2 text-center max-w-md'>
								<span className='text-red-500 text-sm font-medium'>Failed to render preview</span>
								<span className='text-gray-400 text-xs break-words'>{zplPreview.error}</span>
							</div>
						)}
						{!zplPreview.loading && !zplPreview.error && zplPreview.url && (
							<img
								src={zplPreview.url}
								alt='ZPL label preview'
								style={{ transform: `scale(${zoom})`, transformOrigin: 'center', imageRendering: 'pixelated' }}
								className='shadow-md border border-gray-200 bg-white max-w-none'
							/>
						)}
					</div>
				) : (
					<div style={{ transform: `scale(${zoom})`, transformOrigin: 'top left', width: '100%', height: '100%' }}>
						<Rnd
							scale={zoom}
							bounds="parent"
							cancel=".label-child-component"
							position={labelDetails.position}
							size={{
								width: labelDetails.size.width * MM_TO_PX,
								height: labelDetails.size.height * MM_TO_PX,
							}}
							minWidth={60}
							minHeight={30}
							onDragStop={(e, d) => setLabelDetails(prev => ({
								...prev,
								position: { x: d.x, y: d.y },
							}))}
							disableDragging={previewMode}
							enableResizing={false}
							style={{ cursor: previewMode ? 'default' : 'move' }}
						>
							<div className={`bg-white h-full w-full cursor-pointer relative flex-shrink-0 ${!previewMode && selectedComponent?.name === 'label' ? 'shadow-[0_2px_8px_2px_rgba(74,112,251,0.5)]' : 'shadow-md'}`}
								style={{
									border: labelDetails?.borderStyle === 'none'
										? 'none'
										: `${labelDetails?.thickness}px ${labelDetails?.borderStyle} ${labelDetails?.borderColor}`,
								}}
								onClick={() => handleSelectedComponent({ name: 'label' })}
								onMouseDownCapture={(e) => {
									// Component onClick se pehle (capture phase) click point ko label ke
									// model coords me record karo — overlap click-cycling ke liye.
									const rect = e.currentTarget.getBoundingClientRect();
									const z = zoom || 1;
									clickPtRef.current = {
										x: (e.clientX - rect.left) / z,
										y: (e.clientY - rect.top) / z,
									};
								}}
								onMouseMove={handleLabelMouseMove}
								onMouseLeave={handleLabelMouseLeave}
							>
								{!previewMode && guide.visible && (
									<div
										style={{
											position: 'absolute',
											left: guide.x + 10,
											top: guide.y + 10,
											display: 'flex',
											alignItems: 'stretch',
											gap: '1px',
											background: 'rgba(55, 65, 81, 0.82)',
											backdropFilter: 'blur(2px)',
											color: '#fff',
											fontSize: '8px',
											fontWeight: 600,
											lineHeight: 1,
											borderRadius: '4px',
											overflow: 'hidden',
											boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
											pointerEvents: 'none',
											whiteSpace: 'nowrap',
											zIndex: 51,
										}}
									>
										<span style={{ display: 'flex', alignItems: 'center', gap: '2px', padding: '2px 4px' }}>
											<span style={{ color: '#a9c2ff', fontWeight: 700 }}>X</span>
											<span style={{ fontVariantNumeric: 'tabular-nums' }}>{guide.dotX}</span>
										</span>
										<span style={{ display: 'flex', alignItems: 'center', gap: '2px', padding: '2px 4px', background: 'rgba(255,255,255,0.10)' }}>
											<span style={{ color: '#a6e9bd', fontWeight: 700 }}>Y</span>
											<span style={{ fontVariantNumeric: 'tabular-nums' }}>{guide.dotY}</span>
										</span>
									</div>
								)}
								{/* Smart alignment guide lines (drag ke doraan) */}
								{!previewMode && snapLines.v.map((l, i) => (
									<div
										key={`snap-v-${i}`}
										style={{
											position: 'absolute', left: l.pos, top: l.a, width: '1px', height: Math.max(0, l.b - l.a),
											backgroundColor: '#ff2d55', pointerEvents: 'none', zIndex: 60,
										}}
									/>
								))}
								{!previewMode && snapLines.h.map((l, i) => (
									<div
										key={`snap-h-${i}`}
										style={{
											position: 'absolute', top: l.pos, left: l.a, height: '1px', width: Math.max(0, l.b - l.a),
											backgroundColor: '#ff2d55', pointerEvents: 'none', zIndex: 60,
										}}
									/>
								))}
								{currentLabelComponents?.map((labelComp) => {
									const W = Number(labelComp?.size?.width) || 0;
									const H = Number(labelComp?.size?.height) || 0;
									const x = Number(labelComp?.position?.x) || 0;
									const y = Number(labelComp?.position?.y) || 0;
									const rot = ((Math.round((Number(labelComp?.rotate) || 0) / 90) * 90) % 360 + 360) % 360;
									const swap = rot === 90 || rot === 270;
									const boxW = swap ? H : W;
									const boxH = swap ? W : H;
									const boxX = x + (W - boxW) / 2;
									const boxY = y + (H - boxH) / 2;
									// footprint box (bx,by,bw,bh) -> un-rotated model (same center)
									const toModel = (bx, by, bw, bh) => {
										const cx = bx + bw / 2;
										const cy = by + bh / 2;
										const mw = swap ? bh : bw;
										const mh = swap ? bw : bh;
										return {
											size: { width: Math.round(mw), height: Math.round(mh) },
											position: { x: Math.round(cx - mw / 2), y: Math.round(cy - mh / 2) },
										};
									};
									const isSel = selectedComponent?.id === labelComp?.id;
									// line ke liye Paint jaisa: sirf dono end (left/right) par handle,
									// line ke bilkul upar (vertically centered). baaki components corners.
									const isLine = labelComp?.name === 'line';
									const resizeDot = (
										<div style={{ boxSizing: 'border-box', width: 8, height: 8, background: '#fff', border: '1.5px solid #4a70fb', borderRadius: 2, boxShadow: '0 0 2px rgba(0,0,0,0.35)' }} />
									);
									const cornerHandleStyle = { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 10, height: 10, zIndex: 20 };
									const endHandleStyle = { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 12, height: '100%', top: 0, zIndex: 20 };
									const resizeEnable = (previewMode || !isSel)
										? false
										: (isLine
											? { top: false, right: true, bottom: false, left: true, topLeft: false, topRight: false, bottomRight: false, bottomLeft: false }
											: { top: true, right: true, bottom: true, left: true, topLeft: true, topRight: false, bottomRight: true, bottomLeft: true });
									const resizeHandles = !previewMode && isSel
										? (isLine
											? { left: resizeDot, right: resizeDot }
											: { topLeft: resizeDot, bottomLeft: resizeDot, bottomRight: resizeDot })
										: undefined;
									return (
										<Rnd
											key={labelComp?.id}
											scale={zoom}
											className="label-child-component"
											bounds="parent"
											position={{ x: boxX, y: boxY }}
											size={{ width: boxW, height: boxH }}
											minWidth={6}
											minHeight={6}
											enableResizing={resizeEnable}
											resizeHandleComponent={resizeHandles}
											resizeHandleStyles={{
												topLeft: { ...cornerHandleStyle, left: -5, top: -5 },
												bottomLeft: { ...cornerHandleStyle, left: -5, bottom: -5 },
												bottomRight: { ...cornerHandleStyle, right: -5, bottom: -5 },
												left: { ...endHandleStyle, left: -6 },
												right: { ...endHandleStyle, right: -6 },
											}}
											disableDragging={previewMode || editingId === labelComp?.id}
											cancel=".label-text-input"
											onDrag={(e, d) => {
												const r = computeAlign(labelComp?.id, d.x, d.y, boxW, boxH);
												setSnapLines({ v: r.v, h: r.h });
											}}
											onDragStop={(e, d) => {
												const r = computeAlign(labelComp?.id, d.x, d.y, boxW, boxH);
												setSnapLines({ v: [], h: [] });
												handleCurrentLabelComponent(labelComp?.id, toModel(r.x, r.y, boxW, boxH));
											}}
											onResizeStop={(e, direction, ref, delta, position) => handleCurrentLabelComponent(labelComp?.id, toModel(position.x, position.y, ref.offsetWidth, ref.offsetHeight))}
											style={{ zIndex: selectedComponent?.id === labelComp?.id ? 10 : 2 }}
										>
											<div className='group w-full h-full relative'>
												<div
													className='w-full h-full'
													style={swap
														? {
															position: 'absolute',
															top: '50%',
															left: '50%',
															width: W,
															height: H,
															transform: `translate(-50%, -50%) rotate(${rot}deg)`,
															transformOrigin: 'center center',
														}
														: { transform: rot ? `rotate(${rot}deg)` : 'none' }}
												>
													{renderComponent(labelComp)}
												</div>
												{!previewMode
													&& labelComp?.name === 'image'
													&& !labelComp?.src && (
														<button
															type='button'
															title='Delete'
															onClick={(e) => { e.stopPropagation(); handleDeleteComponentById(labelComp?.id); }}
															style={{ width: ctrlIconSize(labelComp), height: ctrlIconSize(labelComp) }}
															className='label-text-input absolute top-1 right-1 z-30 hidden group-hover:flex items-center justify-center rounded-full bg-red-500 text-white shadow hover:bg-red-600'
														>
															<DeleteOutline sx={{ fontSize: Math.round(ctrlIconSize(labelComp) * 0.6) }} />
														</button>
													)}
											</div>
										</Rnd>
									);
								})}
							</div>
						</Rnd>
					</div>
				)}
			</div>
			<Menu
				anchorEl={placeholderAnchor}
				open={Boolean(placeholderAnchor)}
				onClose={() => setPlaceholderAnchor(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
				PaperProps={{ style: { maxHeight: 300 } }}
			>
				{placeholderOptions.length === 0 && (
					<MenuItem disabled>No fields available</MenuItem>
				)}
				{placeholderOptions.map((opt) => (
					<MenuItem key={opt} onClick={() => handleSelectPlaceholder(opt)}>
						{opt}
					</MenuItem>
				))}
			</Menu>
			{/* Image crop modal (image component ke liye) */}
			<ImageCropModal handleConfirm={handleImageInput} />
			{/* Component delete confirmation */}
			<ConfirmationModal
				isOpen={deleteConfirmId != null}
				heading='Delete Component'
				message='Would you like to delete this component?'
				onClose={() => setDeleteConfirmId(null)}
				onConfirm={confirmDeleteComponent}
			/>
			{/* ZPL me width/height na ho to fallback size warning — user khud band karega (no auto-close) */}
			<ConfirmationModal
				isOpen={sizeFallbackUsed}
				isAlert
				alertType='warning'
				autoClose={false}
				showCrossBtn
				heading='Verify label size'
				onClose={() => setSizeFallbackUsed(false)}
				message="This label's ZPL doesn't include a width/height, so the previously saved size has been applied. Please review and edit the dimensions if they don't match your requirement before proceeding."
			/>
		</div>
	);
};

export default LabelGenerator;
